import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { authService } from '../../services/apiServices';

const MfaSetupModal = ({ isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState('loading');
  const [qrCode, setQrCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setStep('loading');
      setError('');
      setTotpCode('');
      setBackupCodes([]);
      loadSetup();
    }
  }, [isOpen]);

  const loadSetup = async () => {
    try {
      const response = await authService.mfaSetup();
      if (response.success) {
        setQrCode(response.data.qrCode);
        setStep('scan');
      } else {
        setError(response.message || 'Erreur de configuration');
      }
    } catch (err) {
      setError(err.data?.message || err.message || 'Erreur de configuration');
    }
  };

  const handleVerify = async () => {
    if (!totpCode || totpCode.length < 6) {
      setError('Veuillez entrer un code à 6 chiffres');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const response = await authService.mfaVerify(totpCode);
      if (response.success) {
        setBackupCodes(response.data.backupCodes);
        setStep('backup');
      } else {
        setError(response.message || 'Code invalide');
      }
    } catch (err) {
      setError(err.data?.message || err.message || 'Code invalide');
    } finally {
      setVerifying(false);
    }
  };

  const handleComplete = () => {
    if (onComplete) onComplete();
    onClose();
  };

  const downloadBackupCodes = () => {
    const content = backupCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ebios-rm-mfa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-800 rounded-2xl border border-gray-700 shadow-2xl w-full max-w-md mx-4 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">
            {step === 'backup' ? 'Codes de secours' : 'Activer MFA'}
          </h3>
          {step !== 'backup' && (
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}

        {step === 'loading' && (
          <div className="flex justify-center py-8">
            <svg className="animate-spin w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        )}

        {step === 'scan' && (
          <div>
            <p className="text-gray-300 text-sm mb-4">
              Scannez ce QR code avec Google Authenticator ou une application compatible, puis entrez le code à 6 chiffres pour activer MFA.
            </p>
            <div className="flex justify-center mb-4">
              <img src={qrCode} alt="QR Code MFA" className="w-48 h-48 rounded-lg bg-white p-2" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Code à 6 chiffres
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="flex-1 py-3 px-4 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-center text-xl tracking-widest font-mono"
                  maxLength={6}
                  autoFocus
                />
                <button
                  onClick={handleVerify}
                  disabled={verifying || totpCode.length < 6}
                  className={`px-6 py-3 rounded-lg font-semibold text-white transition-all ${verifying || totpCode.length < 6
                    ? 'bg-blue-600/50 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-500'}`}
                >
                  {verifying ? '...' : 'Vérifier'}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 'backup' && (
          <div>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
              <p className="text-yellow-400 text-sm font-medium">
                Conservez ces codes de secours dans un endroit sûr. Si vous perdez l'accès à votre application d'authentification, ces codes vous permettront de vous connecter. Chaque code ne peut être utilisé qu'une seule fois.
              </p>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
              {backupCodes.map((code, i) => (
                <div key={i} className="font-mono text-sm text-gray-300 py-1">
                  {i + 1}. {code}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={downloadBackupCodes}
                className="flex-1 py-3 rounded-lg font-semibold bg-gray-700 text-white hover:bg-gray-600 transition-colors"
              >
                Télécharger
              </button>
              <button
                onClick={handleComplete}
                className="flex-1 py-3 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-500 transition-colors"
              >
                Terminé
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default MfaSetupModal;
